import type { StripeCheckoutSession } from '@stripe/stripe-js';

// ProductConfig is the product a publisher-authored product handle resolves to.
// showPaywall({ product }) renders the product and its active prices.
export interface ProductConfig {
  domainId: number;
  // The domain's visitor-identity auth mode, delivered with the onsite config so
  // the SDK can pick its adapter setup path. Absent is treated as the default.
  authMode?: AuthMode;
  // Whether the publisher allows checkout without an account. When false the
  // checkout identity gate hides "Continue as guest". Absent is treated as the
  // default (enabled), matching the backend default.
  guestCheckoutEnabled?: boolean;
  // Whether the publisher has opted into expedited checkout: anonymous visitors
  // skip the identity choices screen and land on a single-screen guest checkout
  // (email field at the top, payment form mounting inline after submit). Only
  // ever true when guestCheckoutEnabled is also true (the backend enforces the
  // pairing). Absent is treated as false, matching the backend default.
  expeditedCheckoutEnabled?: boolean;
  // The publisher's support contact email, surfaced as a "contact us" line in
  // the checkout flow. Absent when the publisher has not configured one.
  supportEmail?: string;
  // Publisher-authored per-domain overrides of the checkout/paywall chrome copy
  // (trust line, "cancel anytime", "manage subscription"). Absent when the
  // domain overrides nothing, so the widget renders its computed default chrome.
  checkoutText?: CheckoutText;
  // The domain's visitor-facing language setting: 'auto' (resolve per visitor
  // from the browser locale) or a fixed locale forced for every visitor.
  // Absent is treated as 'auto', matching the backend default.
  visitorLanguage?: string;
  // Whether this page's checkout offers wallets (Apple Pay, Google Pay, Link
  // and the rest of Stripe's automatic set): the domain's pubdash switch,
  // always true on the shared open.video portal. False ⇒ card only. Absent is
  // treated as true.
  walletsEnabled?: boolean;
  product?: OnsiteProduct;
  // The campaign-code preview, present only when the config request carried a
  // promo param. reason is set when the code can't redeem at all; otherwise
  // discounts carries an entry per applicable price (possibly none). Display-
  // only — checkout applies validate fresh.
  promo?: PromoPreview;
}

// PromoPreview is the plan picker's campaign-code data (see
// ProductConfig.promo).
export interface PromoPreview {
  code: string;
  reason?: string;
  discounts?: Record<string, PromoDiscount>;
}

// PromoDiscount is one price's discounted display: the server-computed
// charged amount (Stripe's rounding, clamped at zero) plus the coupon's
// shape for the duration copy.
export interface PromoDiscount {
  discountedAmountCents: number;
  currency: string;
  percentOff?: number;
  amountOff?: number;
  duration?: string;
  durationInMonths?: number;
}

// CampaignPromoSource is a campaign code's provenance, which drives the
// failure matrix: url = the ?promo= stash (not_found suppressed — foreign
// param), api = the publisher-wired showPaywall/openCheckout option (fails
// loud). Manual entry is the absence of a CampaignPromo.
export type CampaignPromoSource = 'url' | 'api';

// CampaignPromo is the campaign code a mount auto-applies, with its
// provenance.
export interface CampaignPromo {
  code: string;
  source: CampaignPromoSource;
}

// AuthMode is the per-domain visitor-identity auth mode. Default ezauth.
export type AuthMode = 'ezauth' | 'byo';

// DonationsConfig is the domain's active donation products, surfaced site-wide
// for openDonation().
export interface DonationsConfig {
  domainId: number;
  donations: OnsiteProduct[];
  // Whether the publisher has opted into expedited checkout (see ProductConfig).
  // The donation flow has no identity choices screen, so this only switches its
  // checkout to the single-screen email+payment layout. Absent is treated as
  // false.
  expeditedCheckoutEnabled?: boolean;
  // Publisher-authored per-domain checkout/paywall chrome overrides (see
  // ProductConfig). The donation gate renders the paywall trust line, so it
  // reads the same overrides. Absent when the domain overrides nothing.
  checkoutText?: CheckoutText;
  // The domain's visitor-facing language setting (see ProductConfig).
  visitorLanguage?: string;
  // Whether this page's checkout offers wallets (see ProductConfig).
  walletsEnabled?: boolean;
}

// SiteConfig is the domain-level config the widget reads independently of any
// product, so checkout entry points without a product handle (e.g. openCheckout)
// can resolve the identity auth mode. authMode absent is treated as the default.
export interface SiteConfig {
  domainId: number;
  authMode?: AuthMode;
  // Whether the publisher allows checkout without an account (see ProductConfig).
  // Absent is treated as the default (enabled).
  guestCheckoutEnabled?: boolean;
  // Whether the publisher has opted into expedited checkout (see ProductConfig).
  // Absent is treated as false.
  expeditedCheckoutEnabled?: boolean;
  // The publisher's support contact email (see ProductConfig). Absent when the
  // publisher has not configured one.
  supportEmail?: string;
  // Publisher-authored per-domain checkout/paywall chrome overrides (see
  // ProductConfig). Absent when the domain overrides nothing.
  checkoutText?: CheckoutText;
  // The domain's visitor-facing language setting (see ProductConfig).
  visitorLanguage?: string;
  // The environment-scoped Stripe publishable key, exposed early so a checkout
  // surface can prewarm Stripe.js on open instead of waiting for the
  // checkout/trial start response to learn the key.
  publishableKey?: string;
  // True when the serving instance runs test keys, so checkout tolerates an
  // insecure (http) page context — the secure-redirect gate reads it. Absent
  // on older backends is treated as live (interstitial stands).
  sandbox?: boolean;
}

// CheckoutText is the publisher's per-domain override of the checkout/paywall
// chrome copy. Each field is absent when unset, so the widget resolves each
// string as override-or-computed-default independently.
export interface CheckoutText {
  // Replaces the paywall trust line ("Secure checkout by Stripe").
  secureCheckoutNote?: string;
  // Replaces the recurring-paywall "Cancel anytime" portal link label.
  cancelAnytimeLabel?: string;
  // Replaces the recurring-paywall "Manage subscription" link label.
  manageSubscriptionLabel?: string;
}

export interface OnsiteProduct {
  externalId: string;
  productKey: string;
  name: string;
  description: string;
  type: ProductType;
  prices: OnsitePrice[];
  // Publisher-authored per-product paywall header override. Each field is absent
  // when unset, so the widget resolves the eyebrow/title/subtitle as
  // override-or-computed-default independently.
  paywallText?: PaywallText;
  // Publisher-authored per-product paywall appearance (template/mode/accent).
  // Absent when the product uses the default, so the widget renders its built-in
  // appearance. Each field is absent when it equals the built-in default, so the
  // widget resolves template/mode/accent independently.
  theme?: PaywallTheme;
  // Publisher-authored per-product checkout/paywall chrome override. Each field
  // is absent when unset, so the widget resolves each string as product override,
  // then domain default, then computed default independently.
  checkoutText?: CheckoutText;
}

// PaywallText is the publisher's per-product override of the paywall header copy.
export interface PaywallText {
  eyebrow?: string;
  title?: string;
  subtitle?: string;
}

// PaywallTemplate is the paywall layout template: 'classic' (the original look,
// the default) or 'modern' (a refreshed token bundle over the same component
// tree).
export type PaywallTemplate = 'classic' | 'modern';

// PaywallMode is the paywall color mode: 'light' (default), 'dark', or 'auto'
// (follows the visitor's prefers-color-scheme).
export type PaywallMode = 'light' | 'dark' | 'auto';

// PaywallTheme is the publisher's per-product override of the paywall appearance.
// Each field is absent when it equals the built-in default (template=classic,
// mode=light, no accent), so the widget resolves each independently.
export interface PaywallTheme {
  template?: PaywallTemplate;
  mode?: PaywallMode;
  // A six-digit hex accent (e.g. '#5fa624') applied to the CTA and everywhere the
  // --sm-accent token flows. Absent uses the widget's built-in accent.
  accentColor?: string;
}

// Stored and served as 'access' or 'donation'. Recurring vs one-time is a
// price type, not a product type.
export type ProductType = 'access' | 'donation';

export interface OnsitePrice {
  externalId: string;
  priceKey: string;
  // Publisher-authored per-price label and blurb (the builder's "Price label"),
  // shown on the paywall price option. Absent = the widget's computed labeling.
  name?: string;
  description?: string;
  type: string;
  // What a one-time price grants ('product' or 'item') and, for an item-scoped
  // price, where the item comes from ('runtime' = supplied to showPaywall/
  // openCheckout, 'page' = the current page URL). Recurring/donation prices
  // carry the inert defaults. Drive paywall visibility and the checkout item.
  grantScope?: string;
  itemSource?: string;
  currency: string;
  amountCents: number;
  minAmountCents?: number;
  maxAmountCents?: number;
  // Publisher-configured suggested-amount buttons for a variable donation
  // (ascending cents). Absent/empty makes the widget derive them from the
  // minimum (min, 2×, 5×, 10×).
  presetAmountsCents?: number[];
  billingInterval?: string;
  billingIntervalCount?: number;
  // Set on trial-enabled recurring prices: the number of free days before the
  // first charge. Drives the paywall's "Start N-day free trial" CTA and the
  // trial order summary.
  trialPeriodDays?: number;
  // Publisher's free-trial badge template with a "{days}" token (default
  // "{days}-day free trial"); the widget substitutes trialPeriodDays. Blank/absent
  // uses the widget default.
  trialBadgeLabel?: string;
  // Publisher's purchase-button text. Blank/absent uses the widget's computed
  // default ("Subscribe"/"Buy"/"Donate"/"Continue", per product/price type).
  ctaLabel?: string;
  // Publisher's marketing/promo badge text (e.g. "Recommended", "Best value"),
  // rendered in the same head slot as the trial badge. Mutually exclusive with a
  // trial: a trial-bearing price never carries one (the trial badge wins).
  // Blank/absent shows no promo badge.
  badgeLabel?: string;
  // Publisher's chosen paywall highlight: the paywall seeds its selection to
  // the first flagged price (the shared CTA acts on it without a click).
  // Absent/false on every price = the first offered price is seeded.
  preselected?: boolean;
  // Whether the resolving visitor may still start a trial on this price. Set
  // only on trial-enabled prices; optimistic (true) on the cold paywall, where
  // there is no resolved visitor. ConfirmTrial's Gate 1 is the real enforcement.
  trialEligible?: boolean;
  // Preview-only: set by the dashboard builder for an already-saved price, whose
  // billing interval is fixed after creation (changing it archives and recreates
  // the price). The inline preview reads it to render the cadence control as
  // locked. Never sent by the live onsite API — inert in the shipped widget.
  intervalLocked?: boolean;
}

// Credential is the identity a checkout or session-establish call carries: a
// plaintext email (guest or BYO, already on the page), an ezAuth access token
// the server resolves to an email so the plaintext never reaches the page, an
// SM-minted identity token (BYO verified: the publisher's backend fetched it
// from POST /api/v1/identity-token and the page only ever holds the token), or
// a { session: true } marker for a visitor already holding a content session —
// the checkout call then sends the session bearer and the server resolves the
// visitor from it, so no email or token is put on the page.
export type Credential = { email: string } | { ezAuthToken: string } | { identityToken: string } | { session: true };

// ParkedSelection is the checkout selection parked when the visitor leaves
// mid-flow — the core fields every resume transport carries. Two envelopes
// extend it: PendingIntent (session.ts — localStorage, BYO navigate-away
// login) and SecureResumePayload (secureRedirect.ts — ezsm_resume URL param,
// http→https redirect). Add new selection fields here so both stay in step.
export interface ParkedSelection {
  // kind selects the resume path, not the purchase type: 'product' re-mounts
  // the paywall (product handle + chosen price external id); 'checkout' re-runs
  // openCheckout (price key). 'item' is the legacy tag for the openCheckout
  // path — still accepted on read so a pre-deploy parked intent resumes — but
  // new writes use 'checkout' (openCheckout sells subscriptions and trials, not
  // just item purchases, so 'item' was a misnomer).
  kind: 'product' | 'item' | 'checkout';
  // Product paywall: the product handle and the chosen price. A bare paywall
  // "Log in" with no plan picked omits the price.
  product?: string;
  priceExternalId?: string;
  // openCheckout ('checkout', or legacy 'item'): the price key and (for an
  // item-scoped price) the item key.
  price?: string;
  item?: string;
  dismissible?: boolean;
  // The campaign code in force when the selection was parked, so a resume
  // re-applies it. sessionStorage is origin-scoped, so the http→https secure
  // redirect would otherwise drop the URL stash at the hop.
  promo?: CampaignPromo;
}

// TrialSetupResume is a trial card check that finished on Stripe's 3DS
// redirect, parked around confirmSetup and handed back on the return: the
// confirmed SetupIntent, the promo applied on the form (code + validated
// discount), and the trial's Stripe init for a first invoice that needs 3DS.
// Public values only; no buyer credential. idempotencyKey is pinned at park
// time so every resumed confirm (a retry, or a remount on the same load)
// replays one Stripe create instead of minting a second subscription. flow is
// the funnel flow the card check ran in.
export interface TrialSetupResume {
  setupIntentId: string;
  idempotencyKey: string;
  flow: FunnelFlow;
  promoCode?: string;
  discount?: TrialDiscount;
  publishableKey?: string;
  connectedAccount?: string;
}

// ReturnedTrialSetup is the park handed back by Stripe's return, plus the
// return URL's setup_intent_client_secret: proof that this browser ran the
// card check, so a buyer with no credential confirms without re-identifying.
// Memory only (load-scoped); never parked.
export interface ReturnedTrialSetup extends TrialSetupResume {
  setupIntentClientSecret?: string;
}

// Funnel attribution dimensions shared by the event beacons and the
// checkout/trial request bodies (the server-side checkout_completed event
// reads them off session metadata / the finalize request).
export type FunnelSurface = 'paywall' | 'checkout' | 'donation';
export type FunnelFlow = 'classic' | 'expedited';

export interface CheckoutRequest {
  // The product/price the checkout is for. Every current surface (paywall,
  // donation, and openCheckout) resolves these from the fetched config and
  // sends them; the backend also accepts a bare `price` key (below) and
  // resolves it server-side, which no widget caller uses today.
  productExternalId?: string;
  priceExternalId?: string;
  // Exactly one identity credential is sent (see Credential): a guest/BYO email,
  // an ezAuth access token the server resolves to an email server-side, an
  // SM-minted identity token (BYO verified), or the `session` marker (below)
  // for a content-session visitor.
  email?: string;
  ezAuthToken?: string;
  identityToken?: string;
  // When true, the caller sends the content-session bearer and omits email/token
  // from the body; the server resolves the visitor from the session. Stripped
  // from the JSON body by the API client (it selects the Authorization header).
  session?: boolean;
  idempotencyKey: string;
  requestedAmountCents?: number;
  // The currency requestedAmountCents is denominated in — the currency the
  // paywall presented (a currency option when one matched the viewer's geo,
  // else the price default). The server charges in this currency so a geo
  // flap between the config fetch and checkout start can't reinterpret the
  // visitor-chosen amount. Donation checkouts only.
  currency?: string;
  // price is the publisher-authored PriceKey for direct checkout. The backend
  // accepts it in place of the external ids; the widget no longer sends it
  // (openCheckout resolves the key to external ids via price-config first).
  price?: string;
  // item scopes a publisher-driven one-time purchase. It is stored verbatim as
  // the entitlement key and matched exactly by hasPurchased({ item }).
  item?: string;
  // Internal: the API client stamps the platform pageview id (when present) so
  // the server-side checkout_completed event can join the client-side funnel.
  pageviewId?: string;
  // Funnel context for the server-side checkout_completed event: which dialog
  // and which checkout UX (classic multi-step or expedited single screen)
  // drove the checkout. The API client stamps url itself.
  surface?: FunnelSurface;
  flow?: FunnelFlow;
  url?: string;
}

export interface CheckoutResponse {
  checkoutId: string;
  status: string;
  clientSecret: string;
  publishableKey: string;
  provider: string;
  providerAccount?: string;
  // Display-safe mask of the email the checkout runs against, set on every
  // identified start (session, ezAuth, identity token, and client-asserted
  // guest/BYO email alike). Empty only for anonymous deferred (expedited)
  // starts. Rendered on the payment/success steps.
  maskedEmail?: string;
  // One-click portal login link for the success screen, set only for a
  // portal-eligible buyer (verified ezAuth identity, or a brand-new visitor).
  // Empty otherwise, so the success screen falls back to the bare portal URL.
  portalUrl?: string;
  // Whether this checkout session accepts a promo code (the domain has an active
  // promo code and the checkout is not a donation). When true the payment step
  // shows the promo-code entry; absent/false hides it.
  promotionCodesEnabled?: boolean;
  // The session was created with saving enabled: confirm with
  // savePaymentMethod (Stripe.js throws without it).
  savePaymentMethod?: boolean;
}

export interface GrantedEntitlement {
  productId?: number;
  status: string;
}

export interface CheckoutCompletionResponse {
  checkoutId: string;
  status: 'completed' | 'pending';
  providerStatus?: string;
  // Set when a trial gate softly denies (no subscription created, not an HTTP
  // error). Set by the trial gates' soft denials at confirm: 'trial_card_used'
  // (Gate 2: the card already trialed this product) or
  // 'trial_already_redeemed' (Gate 1: this visitor already trialed it). Both
  // route the widget to the convert-to-paid acknowledgment on the card already
  // saved to the customer.
  ineligibleReason?: string;
  // The buyer's masked email at completion, for the success screen's account
  // offer. On the deferred-identity paths (wallet one-tap, Stripe-element email
  // collection) the widget never held the email, so it reads the mask from here.
  // Plaintext never crosses to the widget.
  maskedEmail?: string;
  // A signed, short-lived token attesting that this email completed a checkout
  // on this domain. The widget posts it to sol's register to create an account
  // without the plaintext email touching the page. Present whenever a usable
  // email was resolved; the widget uses it only when its own guest state says
  // this was a guest purchase.
  emailAttestation?: string;
  entitlements: GrantedEntitlement[];
  // One-click portal login link for the success screen, set only when the
  // checkout is the visitor's first activity on the domain (a domain-scoped
  // link can then only expose what this checkout created) — the
  // deferred-identity paths, where the start response had no identity to mint
  // one for (see CheckoutResponse.portalUrl for the start-time twin).
  portalUrl?: string;
  sessionToken?: string;
  sessionExpiresAt?: string;
  // Set only on a pending direct-created subscription (ConfirmTrial /
  // ConvertToPaid) whose first invoice needs on-session authentication (3DS):
  // the invoice's PaymentIntent client secret. The widget confirms it with
  // Stripe.js, then completes via the sub_ checkout id.
  clientSecret?: string;
  // ConvertToPaid pending only, from a fresh read of the first invoice:
  // requires_action (3DS), failed (decline or failed 3DS; failureReason set)
  // or expired (Stripe voided it; start a new convert). paymentMethodId is
  // the saved card to confirm with.
  paymentState?: 'requires_action' | 'failed' | 'expired';
  paymentMethodId?: string;
  failureReason?: 'authentication_failed' | 'card_declined';
  // The first invoice's actual totals, set only on a completed ConvertToPaid
  // (the one success path with no Checkout Session). Lets the success screen
  // show what the buyer was charged rather than the catalog price.
  chargedAmountCents?: number;
  chargedCurrency?: string;
  chargedDiscountCents?: number;
  // The purchased price's catalog type (recurring, one_time, donation_fixed,
  // donation_variable), set on a completed result. A return after a redirect or
  // reload has no price loaded; the success screen picks its copy from this.
  priceType?: string;
}

// IneligibleReasonTrialCardUsed mirrors the backend constant: a Gate-2 soft
// denial that routes the widget to convert-to-paid on the already-saved card.
export const IneligibleReasonTrialCardUsed = 'trial_card_used';

// IneligibleReasonTrialAlreadyRedeemed mirrors the backend constant: a Gate-1
// soft denial (the visitor already trialed). Gate 1 runs only at confirm, so
// the card is already saved by denial time — same convert-to-paid routing as
// Gate 2.
export const IneligibleReasonTrialAlreadyRedeemed = 'trial_already_redeemed';

// TrialStartResponse opens a SetupIntent-first trial: the widget mounts a
// Payment Element on the client secret to capture (and validate) a card without
// charging it, then confirms the trial. Mirrors CheckoutResponse minus the
// checkout id (a trial has a SetupIntent, not a Checkout Session).
export interface TrialStartResponse {
  setupIntentId: string;
  status: string;
  clientSecret: string;
  publishableKey: string;
  provider: string;
  providerAccount?: string;
  // Display-safe mask of the email the trial runs against; see
  // CheckoutResponse.maskedEmail.
  maskedEmail?: string;
  // One-click portal login link for the success screen; see
  // CheckoutResponse.portalUrl.
  portalUrl?: string;
  // Whether the domain has an active promo code, so the trial step shows the
  // promo entry (mirrors CheckoutResponse.promotionCodesEnabled). Trials have no
  // Checkout Session, so the code is checked via validatePromoCode and attached
  // at confirmTrial.
  promotionCodesEnabled?: boolean;
  // Passed to elements() so the Payment Element offers the visitor's saved
  // cards; absent on a deferred (anonymous) start.
  customerSessionClientSecret?: string;
}

// TrialStartRequest opens a SetupIntent-first trial. Mirrors CheckoutRequest's
// product/price + credential shape, minus the one-time/donation fields (trials
// are recurring-only).
export interface TrialStartRequest {
  productExternalId?: string;
  priceExternalId?: string;
  email?: string;
  ezAuthToken?: string;
  identityToken?: string;
  // See CheckoutRequest.session: sends the content-session bearer instead of an
  // email/token, stripped from the body by the API client.
  session?: boolean;
  idempotencyKey: string;
}

// TrialFinalizeRequest confirms a trial or converts a denied trial to paid; both
// carry the SetupIntent the card was captured on.
export interface TrialFinalizeRequest extends TrialStartRequest {
  setupIntentId: string;
  // Sent instead of a credential on a 3DS-return resume (see
  // ReturnedTrialSetup); the server ignores it when a credential is present.
  setupIntentClientSecret?: string;
  // Internal: the API client stamps the platform pageview id (when present) so
  // the server-side checkout_completed event can join the client-side funnel.
  pageviewId?: string;
  // Funnel context for the server-side checkout_completed event (see
  // CheckoutRequest). The API client stamps url itself.
  surface?: FunnelSurface;
  flow?: FunnelFlow;
  url?: string;
  // Optional customer-entered promo code applied on the trial step. The backend
  // resolves and validates it, then attaches the discount to the trialing
  // subscription so it applies to invoices after the trial. Ignored by
  // convert-to-paid, which shares this shape.
  promoCode?: string;
}

// TrialDiscount is a promo resolved on the trial form, which has no live
// Checkout Session to recompute totals. The summary uses it to note that a
// discount will be applied and how long it lasts (once = first charge,
// repeating = the exact month count when known, forever = every charge) — it
// never computes a discounted total. It also rides to the trial-denial
// acknowledgment, whose convert-to-paid is an immediate discounted charge, and
// to the success screen. amountOff is minor units in currency; a fixed
// discount only applies when the currency matches the price.
export interface TrialDiscount {
  percentOff?: number;
  amountOff?: number;
  currency?: string;
  duration: 'once' | 'repeating' | 'forever';
  durationInMonths?: number;
}

// ChargedTotals is a completed convert-to-paid's first-invoice totals (the one
// success path with no Checkout Session): what the buyer was actually charged,
// with the discount amount broken out.
export interface ChargedTotals {
  amountCents: number;
  currency: string;
  discountCents?: number;
}

// CheckoutSuccessDetails is the display context the payment forms hand to the
// flow alongside the completion, so the success screen reflects what the buyer
// actually paid rather than the catalog price: the paid path's final Checkout
// Session (its totals include discounts and tax) or the trial/convert path's
// applied promo (code + validated discount — those paths have no session to
// read them from). charged carries the first invoice's actual totals on the
// convert-to-paid path (the one success with neither a session nor a $0 trial
// charge).
export interface CheckoutSuccessDetails {
  session?: StripeCheckoutSession;
  promoCode?: string | null;
  discount?: TrialDiscount | null;
  charged?: ChargedTotals;
}

// PromoValidationResponse is the pre-submit promo check (trial step, and the
// paid step which needs the stripeCode for applyPromotionCode): whether the
// entered code applies to the selected product/price and, when it does, the
// discount to render on the applied chip. Reason carries a machine code when
// valid is false so the widget can show the right message.
export interface PromoValidationResponse {
  valid: boolean;
  reason?: string;
  code?: string;
  // The namespaced Stripe-side code string. The paid path must hand this (not
  // the typed code) to applyPromotionCode — Stripe matches code strings
  // account-wide. Absent when the code wasn't found.
  stripeCode?: string;
  percentOff?: number;
  amountOff?: number;
  currency?: string;
  // How long the discount stays in effect once the trial converts, mirroring the
  // coupon's Stripe duration. Drives the trial step's discount note (never a
  // computed post-trial total): "once" discounts only the first charge,
  // "repeating" a limited time, "forever" every charge.
  duration?: 'once' | 'repeating' | 'forever';
  durationInMonths?: number;
  // Stripe's invoice-preview totals for an immediate paid subscription with the
  // code attached — what the trial-denial acknowledgment's convert would charge
  // today. Absent when the code is invalid or the preview failed.
  firstChargeAmountCents?: number;
  firstChargeDiscountCents?: number;
  firstChargeCurrency?: string;
}

// PromoValidationRequest is the trial step's promo pre-check: the entered code
// plus the product/price it would apply to. includeFirstChargePreview asks for
// Stripe's invoice-preview totals of an immediate convert charge — only the
// trial paths set it (the trial-denial acknowledgment shows them); the paid
// path renders totals from the live Checkout Session and skips the extra call.
export interface PromoValidationRequest {
  productExternalId: string;
  priceExternalId: string;
  code: string;
  includeFirstChargePreview?: boolean;
}

export interface VerifyResponse {
  jwt: string;
  expiresAt: string;
  // Sandbox only: the preview token, when the link was minted inside a preview.
  preview?: { jwt: string; expiresAt: string };
}

// EstablishSessionResponse is the result of exchanging an adapter-asserted email
// for a content session. authenticated is false (with no jwt) when the email is
// not a known visitor; the widget treats that as anonymous.
export interface EstablishSessionResponse {
  authenticated: boolean;
  jwt?: string;
  expiresAt?: string;
}

export interface AccessResponse {
  decision: 'allowed' | 'denied' | 'login_required' | 'expired' | 'revoked' | 'unknown_product';
  reasonCode: string;
}

// Purchase is one row of the visitor's purchases listing (getPurchases): a held
// product (subscription or whole-product one-time) or a one-time per-item
// purchase. productKey is the product the purchase belongs to (omitted if the
// product was removed); item is set only for a per-item purchase. status +
// expiresAt let the publisher filter active vs expired and label
// library/downloads pages. Unlike getProducts, this includes item-scoped
// purchases.
export interface Purchase {
  productKey?: string;
  item?: string;
  status: string;
  expiresAt?: string;
}

export interface MagicLinkRequest {
  email: string;
  domainId: number;
  returnUrl: string;
}

// 'login' is the passwordless-first sign-in screen (magic-link email primary,
// the ezAuth password form opt-in); 'magiclink' is the sign-in-link
// send/confirmation step (the LoginStep).
export type CheckoutStep = 'idle' | 'secureRedirect' | 'email' | 'payment' | 'trialPayment' | 'trialIneligible' | 'success' | 'login' | 'magiclink' | 'error' | 'localdev';

// CheckoutResult is the payload passed to a publisher's onSuccess callback once
// a checkout completes and access is established.
export interface CheckoutResult {
  productType?: ProductType;
  // Set for product/donation paywall checkouts; omitted for direct checkout,
  // whose product and price are resolved server-side rather than chosen
  // client-side.
  productExternalId?: string;
  priceExternalId?: string;
  // The base amount in the price's currency's minor units: the visitor's chosen
  // amount for a variable donation, otherwise the catalog price. This is the
  // requested amount, not the final total Stripe charged (tax, discounts, and
  // Adaptive Pricing can change that). Omitted for a per-item purchase, whose
  // amount is set server-side and not known to the widget.
  amountCents?: number;
  // Set when showPaywall was opened for a product ({ product }); the
  // publisher-authored product handle the paywall was opened for.
  product?: string;
  // Set for openCheckout({ price }); the PriceKey the checkout was completed for.
  price?: string;
  // The item an item-scoped purchase completed for: openCheckout({ price, item }),
  // or a paywall item-scoped price (runtime item from showPaywall({ product, item })
  // or the current page for a page-sourced price). Omitted for whole-product buys.
  item?: string;
}

// CheckoutError is passed to a publisher's onError callback when a checkout
// attempt fails. The flow stays open for retry, so onError may fire more than
// once before a later attempt succeeds or the visitor leaves.
export interface CheckoutError {
  message: string;
  // Stripe.js error.code (e.g. card_declined) when the failure came from the
  // provider; absent for local/backend failures.
  code?: string;
  // The tender behind the failure: 'card' on the form path, the wallet type
  // (apple_pay / google_pay / link) on a wallet confirm. Feeds the funnel's
  // payment_failed payment_method stamp; absent when the failure never
  // reached a tender.
  paymentMethod?: string;
}

// Publisher checkout-lifecycle hooks, accepted by showPaywall, openCheckout, and
// openDonation. All are optional and invoked at most defensively (a throwing hook
// is logged, never allowed to break the widget).
export interface CheckoutCallbacks {
  // Fires once after checkout completes and access is established.
  //
  // @deprecated Best-effort only: the hook is a closure on this page's checkout
  // instance, so a full-page redirect (3DS, BYO navigate-away login) drops it —
  // it never fires when the visitor returns. For anything that must happen when
  // access is granted, subscribe to access:change and re-check with
  // hasAccess/hasPurchased. (Not deprecated on openDonation: donations grant no
  // entitlement, so the access:change a donation emits cannot identify it.)
  onSuccess?: (result: CheckoutResult) => void;
  // Fires once when the visitor dismisses the surface without completing
  // checkout. Only reachable when the surface is dismissible — the default for
  // every surface; pass { dismissible: false } for a blocking gate.
  onCancel?: () => void;
  // Fires when a checkout attempt fails. May fire multiple times across retries.
  onError?: (error: CheckoutError) => void;
}

// AuthAdapter is the credential-shaped identity contract the SDK consumes. In BYO
// mode the publisher supplies it via ezsubscriptions.initialize({ auth }); in
// ezAuth mode the SDK builds one internally (later phase). The BYO variant yields
// a plaintext email (or, when getIdentityToken is provided, an SM-minted identity
// token); the SDK never exposes it back to the page.
export interface AuthAdapter {
  // Optional. Returns a short-lived, SM-minted identity token for the logged-in
  // visitor — fetched on demand from the publisher's own session-authenticated
  // backend endpoint, which obtains it from SM's POST /api/v1/identity-token.
  // When provided and it yields a token, the token is the identity credential
  // (takes precedence over getUserEmail) and the plaintext email never enters
  // page JS. Return null/empty when anonymous (or on a token-fetch failure) to
  // fall back to getUserEmail.
  // May be sync or async.
  //
  // MUST return a fresh token per call: tokens expire after 1 hour, the SDK
  // calls this at the moment of use, and once more if establish rejects the
  // first token as stale. Never cache or render-inject a token.
  getIdentityToken?: () => string | null | Promise<string | null>;
  // Returns a syntactically valid plaintext email when the visitor is logged into
  // the publisher's auth, or null/empty/invalid when anonymous (the SDK then
  // shows the guest EmailStep). May be sync or async. Ignored when
  // getIdentityToken yields a token.
  //
  // One of getUserEmail or getIdentityToken is required. Provide getIdentityToken
  // alone to keep the email off the page entirely; provide getUserEmail alone for
  // the simplest integration; provide both to prefer the token with the email as
  // a fallback.
  getUserEmail?: () => string | null | Promise<string | null>;
  // Sends the visitor to the publisher's login — navigation, modal, or provider
  // SDK call, the adapter decides. Powers the "Already subscribed? Log in" action.
  goToLogin: () => void | Promise<void>;
  // Sends the visitor to the publisher's account creation — navigation, modal, or
  // provider SDK call, the adapter decides. Powers the gate's "Create account"
  // action. May point at the same destination as goToLogin when the publisher's
  // login page also registers.
  goToCreateAccount: () => void | Promise<void>;
  // Signs the visitor out of the publisher's own auth — navigation, modal, or
  // provider SDK call, the adapter decides; may navigate away. Powers the "Not
  // you?" switch next to the checkout identity note. Optional: when absent the
  // switcher is hidden on BYO domains (the SDK cannot sign out the publisher's
  // auth itself).
  logout?: () => void | Promise<void>;
}

// AccessChangeHandler runs when the visitor's access may have changed (login,
// logout, account switch). It takes no arguments; the handler re-reads access
// via hasAccess/hasPurchased and re-renders gated content.
export type AccessChangeHandler = () => void;

// Unsubscribe removes a previously registered handler. Returned by on so callers
// can detach without holding the original handler reference.
export type Unsubscribe = () => void;

export type MountCheckoutError = Error & {
  code: 'invalid_target' | 'invalid_price' | 'invalid_success_url' | 'checkout_active';
};

export interface MountCheckoutOptions {
  target: HTMLElement | string;
  price: string;
  item?: string;
  email?: string;
  promoCode?: string;
  successUrl?: string;
  onError?: (error: CheckoutError) => void;
}

export interface MountedCheckout {
  destroy(): void;
}

// Publisher-facing window API for checkout, identity, and access.
export interface EzSubscriptionsApi {
  // Command queue (Google Publisher Tag pattern). Callbacks pushed before the
  // script loads run in order once it does; after load, push runs immediately.
  cmd: EzSubscriptionsReadyCallback[];
  // true once the script has loaded and this API is live (matches the
  // ezstandalone/ezRewardedAds `ready` convention). Publishers use either cmd
  // (run ASAP) or `if (ready)` (run at a chosen moment).
  ready: boolean;
  // Registers the publisher's BYO AuthAdapter. Call inside cmd.push (or after
  // ready). Safe to call repeatedly: a later valid call replaces the adapter.
  initialize: (config: { auth: AuthAdapter }) => void;
  // Signals that the publisher's auth state changed (login, logout, account
  // switch, async session restore). Re-resolves identity through the adapter,
  // refreshes or clears the SM content session, and fires access:change. Call
  // this instead of re-running initialize for runtime auth changes.
  authChanged: () => Promise<void>;
  // Subscribe to access:change so gated UI re-renders when access may have
  // changed. Returns an unsubscribe function (call it on cleanup); off is the
  // equivalent for callers that prefer to detach by handler reference.
  on: (event: 'access:change', handler: AccessChangeHandler) => Unsubscribe;
  off: (event: 'access:change', handler: AccessChangeHandler) => void;
  // Ad removal for an entitled subscriber. The publisher decides entitlement with
  // their own hasAccess check, then calls disableAds() when access is allowed and
  // allowAds() otherwise (typically from an access:change handler). disableAds
  // mints a signed token and stores it in a reserved cookie Sol verifies to
  // suppress all ads server-side, reloading once on the page where access is first
  // gained; allowAds removes it (ads return on the next navigation, no reload).
  // The widget owns the cookie's lifecycle. disableAds is async (it mints the
  // token); callers may ignore the returned promise.
  disableAds: () => Promise<void>;
  allowAds: () => void;
  // Publisher-driven product access check. Accepts the product handle directly
  // or as { product }.
  // Anonymous visitors short-circuit to login_required with no network call.
  hasAccess: (query: string | { product: string }) => Promise<AccessResponse>;
  // Publisher-driven one-time purchase check: has the visitor bought this item
  // anywhere on the domain? Pass { item } to check a publisher-named item, or
  // { page: true } to check the current page (a "current article automatically"
  // price) — the widget resolves the normalized page key it stamped at checkout,
  // so the publisher never handles it. item is a domain-global key, independent
  // of price/product. Anonymous visitors short-circuit to login_required with no
  // network call. The read counterpart to openCheckout({ price, item }) /
  // showPaywall({ product }).
  hasPurchased: (query: { item: string } | { page: true }) => Promise<AccessResponse>;
  // The product handles the visitor currently holds. Empty for anonymous visitors.
  getProducts: () => Promise<string[]>;
  // The visitor's purchases (subscriptions + one-time item purchases) for
  // building "your purchases"/downloads/library pages. Includes item-scoped
  // purchases that getProducts omits; each row carries status + expiresAt so the
  // publisher can filter active vs expired. Empty for anonymous visitors.
  getPurchases: () => Promise<Purchase[]>;
  // showPaywall presents a publisher-authored product and its prices as a
  // paywall. A product handle is required. item opts a runtime item-scoped price
  // into the paywall (showPaywall({ product, item })): without it, runtime
  // item-scoped prices are hidden; page-sourced item prices always show and
  // source their item from the current page. Dismissible by default; pass
  // { dismissible: false } for a blocking page gate. email optionally prefills
  // the guest email field (see openCheckout). promoCode auto-applies a campaign
  // code: the plan picker shows discounted prices and checkout applies it.
  // Publisher-wired codes fail loud — an invalid code shows inline and logs a
  // console error (unlike the ?promo= URL param, which suppresses unknown
  // codes). Checkout never blocks on a bad code. anchor (element or CSS
  // selector) centers the dialog over that element, e.g. the video it unlocks,
  // while it stays on screen; phones and an off-screen anchor keep the
  // viewport-centered layout.
  showPaywall: (opts?: { product?: string; offer?: string; item?: string; dismissible?: boolean; email?: string; promoCode?: string; anchor?: string | Element } & CheckoutCallbacks) => Promise<void>;
  // openCheckout launches checkout for a configured PriceKey. item is optional:
  // when supplied, it scopes a one-time purchase entitlement; when omitted, the
  // checkout grants the selected product normally. Dismissible by default; pass
  // { dismissible: false } to block dismissal. email optionally prefills the
  // guest email field (e.g. a newsletter signup the page already collected); the
  // visitor still confirms it before payment. promoCode auto-applies a campaign
  // code (see showPaywall). anchor: see showPaywall.
  openCheckout: (opts: { price: string; item?: string; dismissible?: boolean; email?: string; promoCode?: string; anchor?: string | Element } & CheckoutCallbacks) => Promise<void>;
  // Mounts inline checkout; resolves its destroy handle before configuration loads.
  mountCheckout: (opts: MountCheckoutOptions) => Promise<MountedCheckout>;
  // openDonation is the single donation entry point. Self-mounting: it loads the
  // domain's donation config and mounts the launcher on first call, so it works
  // without any setup call and regardless of order. opts can jump to a specific
  // donation (productId), preselect a valid amount (amountCents), and carry
  // per-open checkout callbacks. A no-op (logs a warning) on a domain with no
  // donations configured. A declarative [data-ezoic-donate] element opens the
  // same dialog with no JS.
  openDonation: (opts?: OpenDonationOptions) => Promise<void>;
  // Closes the donation dialog if it is open; a no-op otherwise.
  closeDonation: () => void;
  // showDonations is a deprecated no-op, kept so existing embeds that still call
  // it don't throw. Donations now open via openDonation() or a declarative
  // [data-ezoic-donate] element — both self-mount the launcher — so no setup call
  // is needed. The legacy options bag is accepted and ignored.
  showDonations: (opts?: CheckoutCallbacks) => Promise<void>;
  // Opens a login surface so a returning subscriber can sign in without first
  // hitting a paywall — wire it to a "Log in" link in the site nav, or use the
  // declarative [data-ezoic-login] attribute. On ezAuth domains it renders
  // Ezoic's login screen (ezAuth form + Continue with Google + email sign-in
  // link); on BYO domains it forwards to the publisher's own goToLogin(). On a
  // successful login the content session is established and access:change fires
  // so gated content unlocks in place. Defaults to dismissible. anchor: see
  // showPaywall.
  login: (opts?: { dismissible?: boolean; anchor?: string | Element }) => Promise<void>;
  // Ends the visitor's content session — the counterpart to login(), wired to a
  // "Log out" link in the site nav or the declarative [data-ezoic-logout]
  // attribute. Drops the SM content session, removes the ad-free cookie so ads
  // return on the next navigation, and fires access:change so gated content
  // re-locks in place. On ezAuth domains it also signs the visitor out of Ezoic
  // Visitor Accounts, so they are not re-established on the next load. On BYO
  // domains the publisher signs the visitor out of their own login system first;
  // this clears the content session immediately afterwards.
  logout: () => Promise<void>;
  // Returns the signed-in visitor's session JWT, or null when anonymous/expired.
  // The client half of the REST API reader-token path: a publisher whose server
  // has no reader email (Ezoic-visitor-account sites) relays this token to their
  // backend to forward as X-Ezoic-Reader-Token. Domain-scoped and short-lived;
  // read it per request rather than caching.
  getSessionToken: () => string | null;
  // Resolves to the sandbox preview (gate) token while the publisher previews
  // the sandbox, else null (live). For the WordPress plugin, which forwards it so
  // sandbox SM accepts reader calls during a preview. Never the visitor session.
  // Resolves after session bootstrap; read it per request rather than caching.
  getSandboxPreviewToken: () => Promise<string | null>;
  // Opens the subscriber portal (subscriber.ezoic.com) in a new tab where a
  // logged-in member manages payment methods, receipts, and subscriptions. A
  // trusted ezAuth/BYO member lands already signed in (one-click login link,
  // scoped to your site); guests get the bare portal, which runs its own email
  // sign-in. Publishers wire this to a "Manage subscription" link for members.
  openAccountPortal: () => void;
  // Emits a publisher-defined analytics event (free-form name + optional
  // value) into the domain's custom-event stream — e.g. trackEvent('cta_click',
  // 'homepage-hero'). Fire-and-forget, never throws. Like every API method,
  // call it inside a cmd.push callback (or after ready) — before min.js
  // loads, window.ezsubscriptions is only the cmd queue.
  trackEvent: (name: string, value?: string) => void;
  hide: () => void;
}

// Options for opening the donation dialog from publisher code. amountCents is a
// preselected amount only: the backend donation config still enforces the
// minimum, and an out-of-range amount falls back to normal picker selection.
// The checkout callbacks apply only to the donation opened by this call; a
// donation opened without them (e.g. a declarative [data-ezoic-donate] click)
// runs no callbacks.
export interface OpenDonationOptions extends CheckoutCallbacks {
  productId?: string;
  amountCents?: number;
}

// DonationControls is the imperative handle the mounted DonationLauncher
// publishes to the SDK so window.ezsubscriptions.openDonation/closeDonation can
// drive the single dialog. Kept off the public API surface: publishers call the
// stable openDonation/closeDonation methods, not this.
export interface DonationControls {
  open: (opts?: OpenDonationOptions) => void;
  close: () => void;
}

export type EzSubscriptionsReadyCallback = (api: EzSubscriptionsApi) => void;

declare global {
  interface Window {
    // Before the script loads, publishers seed this with a cmd array and push
    // callbacks onto it. After load it is the resolved EzSubscriptionsApi object
    // (which still carries cmd).
    ezsubscriptions?: EzSubscriptionsApi | { cmd: EzSubscriptionsReadyCallback[] };
  }
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  error?: string;
}
